import React from "react";
import { fmtKwp } from "../../lib/format";
import { Zap, CalendarRange, CalendarX2, Layers } from "lucide-react";
import ClassSplitBar from "./ClassSplitBar";
import DateInput from "../../components/DateInput";

/**
 * PipelineByYear — year rollup panel above the stage summary strip.
 *
 * Buckets the currently-filtered pipeline by year. Completed/operational
 * sites are bucketed by their completion (handover) date, in-flight sites by
 * the chosen planning date source — so historical sites sit under the year
 * they were completed and never inflate the current pipeline. Renders one
 * tile per year present in the data, plus "All years" and "TBC".
 */
export default function PipelineByYear({
  kwpByYear, yearFilter, setYearFilter,
  dateSource, setDateSource,
  customRange, setCustomRange,
}) {
  const { thisYear, buckets, quartersByYear, yearList } = kwpByYear;
  const activeCustom = !!(customRange.from || customRange.to);
  const clearAll = () => { setYearFilter("all"); setCustomRange({ from: "", to: "" }); };

  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const shift = (days) => { const d = new Date(today); d.setDate(d.getDate() + days); return iso(d); };
  const presets = [
    { key: "next30", label: "Next 30d", range: { from: iso(today), to: shift(30) } },
    { key: "next60", label: "Next 60d", range: { from: iso(today), to: shift(60) } },
    { key: "next90", label: "Next 90d", range: { from: iso(today), to: shift(90) } },
    { key: "h1", label: `H1 ${thisYear}`, range: { from: `${thisYear}-01-01`, to: `${thisYear}-06-30` } },
    { key: "h2", label: `H2 ${thisYear}`, range: { from: `${thisYear}-07-01`, to: `${thisYear}-12-31` } },
  ];
  const isPresetActive = (r) => customRange.from === r.range.from && customRange.to === r.range.to;

  // Build the tile list: All · each year (newest first) · TBC.
  const b = (k) => buckets[k] || { kwp: 0, count: 0 };
  const allKwp = yearList.reduce((a, y) => a + b(y).kwp, 0) + b("unscheduled").kwp;
  const allCount = yearList.reduce((a, y) => a + b(y).count, 0) + b("unscheduled").count;
  const tiles = [
    { key: "all", label: "All years", sub: "Whole estate", icon: <Layers size={14} />,
      kwp: allKwp, count: allCount },
    ...yearList.map(y => ({
      key: String(y), label: `${y}`,
      sub: y === thisYear ? "This year" : (y > thisYear ? "Future" : "Historical"),
      icon: <CalendarRange size={14} />, accent: y === thisYear, ...b(y),
    })),
    { key: "unscheduled", label: "TBC", sub: "Unscheduled", icon: <CalendarX2 size={14} />,
      muted: true, ...b("unscheduled") },
  ];
  const toggle = (k) => setYearFilter(yearFilter === k ? "all" : k);

  const selYear = (yearFilter && /^\d{4}$/.test(yearFilter)) ? Number(yearFilter) : thisYear;
  const quarters = quartersByYear[selYear];
  const anySelYear = quarters &&
    (quarters.q1.count + quarters.q2.count + quarters.q3.count + quarters.q4.count) > 0;

  return (
    <div className="mb-6" data-testid="pm-pipeline-by-year">
      <div className="flex flex-wrap items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-3">
          <div className="label">Pipeline by year</div>
          <div className="inline-flex rounded border border-[var(--line)] overflow-hidden text-[10px] font-semibold" data-testid="pm-date-source-toggle">
            {[
              { v: "installation_date", l: "Install date" },
              { v: "handover_date", l: "Handover date" },
            ].map(opt => (
              <button
                key={opt.v}
                onClick={() => setDateSource(opt.v)}
                className={`px-2.5 py-1 uppercase tracking-wider transition-colors ${dateSource === opt.v ? "bg-[var(--ink)] text-white" : "bg-white hover:bg-[var(--bg-2)]"}`}
                data-testid={`pm-date-source-${opt.v}`}
                title="Planning date source for in-flight sites. Completed sites always bucket by handover date."
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {(yearFilter !== "all" || activeCustom) && (
            <button
              onClick={clearAll}
              className="text-[11px] font-semibold text-[var(--accent-hover)] hover:underline"
              data-testid="pm-year-clear"
            >
              Show all years
            </button>
          )}
          <div className="text-[11px] muted italic">Completed sites bucket by completion year</div>
        </div>
      </div>

      {/* Custom date-range picker + presets */}
      <div className="flex flex-wrap items-center gap-2 mb-3 p-2.5 bg-[var(--bg-2)] rounded border border-[var(--line)]" data-testid="pm-custom-range">
        <span className="text-[10px] uppercase tracking-wider muted font-semibold ml-1">Custom range</span>
        <label className="flex items-center gap-1 text-[11px]">
          from
          <DateInput value={customRange.from}
            onChange={(e) => { setCustomRange({ ...customRange, from: e.target.value }); setYearFilter("all"); }}
            className="input-swiss py-1 text-xs" data-testid="pm-range-from" />
        </label>
        <label className="flex items-center gap-1 text-[11px]">
          to
          <DateInput value={customRange.to}
            onChange={(e) => { setCustomRange({ ...customRange, to: e.target.value }); setYearFilter("all"); }}
            className="input-swiss py-1 text-xs" data-testid="pm-range-to" />
        </label>
        <span className="text-[10px] muted mx-1">or</span>
        {presets.map(pre => {
          const active = isPresetActive(pre);
          return (
            <button type="button" key={pre.key}
              onClick={() => { setCustomRange(active ? { from: "", to: "" } : pre.range); setYearFilter("all"); }}
              className={`text-[11px] px-2.5 py-1 rounded border transition-colors ${active ? "bg-[var(--accent)] text-white border-[var(--accent)]" : "border-[var(--line)] hover:border-[var(--accent)]"}`}
              data-testid={`pm-preset-${pre.key}`}>
              {pre.label}
            </button>
          );
        })}
        {activeCustom && (
          <button type="button" onClick={() => setCustomRange({ from: "", to: "" })}
            className="text-[10px] muted hover:text-[var(--danger,#D64545)] ml-1" data-testid="pm-range-clear">
            × clear range
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3" data-testid="pm-year-tiles">
        {tiles.map(t => {
          const active = yearFilter === t.key || (t.key === "all" && yearFilter === "all");
          return (
            <button
              type="button"
              key={t.key}
              onClick={() => t.key === "all" ? setYearFilter("all") : toggle(t.key)}
              className={`p-4 border rounded-lg text-left transition-all flex-1 min-w-[150px] ${
                active
                  ? "bg-[var(--accent)] border-[var(--accent)] text-white shadow-md"
                  : t.accent
                    ? "bg-[var(--accent-soft,#DDF3F1)] border-[var(--accent-hover)] hover:shadow-sm"
                    : "bg-white border-[var(--line)] hover:border-[var(--accent)]"
              } ${t.muted && !t.count && !active ? "opacity-60" : ""}`}
              data-testid={`pm-year-tile-${t.key}`}
              aria-pressed={active}
            >
              <div className="flex items-center gap-1.5 mb-1"
                   style={{ color: active ? "#fff" : (t.accent ? "var(--accent-hover)" : "var(--ink-2)") }}>
                {t.icon}
                <div className="text-[10px] uppercase tracking-[0.14em] font-semibold">{t.sub}</div>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-display text-2xl font-bold leading-tight"
                     style={{ color: active ? "#fff" : (t.accent ? "var(--accent-hover)" : "var(--ink)") }}>
                  {t.label}
                </div>
                <div className="text-right">
                  <div className="font-mono text-base font-semibold flex items-center gap-1 justify-end"
                       style={{ color: active ? "#fff" : (t.accent ? "var(--accent-hover)" : "var(--ink)") }}>
                    <Zap size={12} /> {fmtKwp(t.kwp)}
                  </div>
                  <div className={`text-[10px] mt-0.5 ${active ? "text-white/80" : "muted"}`}>
                    {t.count} project{t.count === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
              {t.key !== "all" && <ClassSplitBar retro={t.retro} dev={t.development} customer={t.customer} active={active} />}
            </button>
          );
        })}
      </div>

      {/* Quarterly breakdown for the selected year */}
      {anySelYear && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-[0.14em] muted font-semibold mb-2 ml-1">
            {selYear} · Quarterly breakdown
          </div>
          <div className="grid grid-cols-4 gap-2" data-testid="pm-year-quarters">
            {[1, 2, 3, 4].map(qn => {
              const qb = quarters[`q${qn}`];
              const key = `${selYear}-q${qn}`;
              const active = yearFilter === key;
              const empty = qb.count === 0;
              return (
                <button type="button" key={qn} onClick={() => toggle(key)}
                  className={`p-3 border rounded text-left transition-all ${
                    active
                      ? "bg-[var(--accent)] border-[var(--accent)] text-white"
                      : empty
                        ? "bg-white border-[var(--line)] opacity-60 hover:opacity-90"
                        : "bg-white border-[var(--line)] hover:border-[var(--accent)]"
                  }`}
                  data-testid={`pm-quarter-tile-${qn}`}
                  aria-pressed={active}>
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-wider font-semibold"
                         style={{ color: active ? "#fff" : "var(--ink-2)" }}>
                      Q{qn}
                    </div>
                    <div className={`text-[9px] ${active ? "text-white/80" : "muted"}`}>
                      {qb.count} proj{qb.count === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="font-mono text-sm font-semibold mt-0.5 flex items-center gap-1"
                       style={{ color: active ? "#fff" : (empty ? "var(--ink-2)" : "var(--accent-hover)") }}>
                    <Zap size={11} /> {fmtKwp(qb.kwp)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
